import type { MfaMethod } from '@crm/shared';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, ApiError } from '../lib/api';

const SSO_ERRORS: Record<string, string> = {
  expired: 'Single sign-on took too long. Try again.',
  denied: 'Sign-in was cancelled at your identity provider.',
  email_not_verified: 'Your identity provider didn’t confirm your email address.',
  domain_not_allowed: 'Your identity provider signed you in with an email this organization doesn’t use.',
  no_access: 'You don’t have access to this organization yet. Ask an administrator to invite you.',
  provider_error: 'Your identity provider couldn’t be reached. Try again, or contact your administrator.',
  invalid_response: 'The sign-in response couldn’t be verified. Try again, or contact your administrator.',
  ip_not_allowed: 'Your organization doesn’t allow sign-in from this network.',
};

/** After SSO, users who set up their own second step arrive with `#mfa=<challenge>&methods=…`. */
function ssoMfaFromFragment(): { challenge: string; methods: MfaMethod[] } | null {
  const p = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const challenge = p.get('mfa');
  if (!challenge) return null;
  return { challenge, methods: (p.get('methods') ?? 'totp').split(',') as MfaMethod[] };
}

const errorText = (err: unknown) => (err instanceof ApiError ? err.message : err instanceof Error && err.name === 'NotAllowedError' ? 'Passkey sign-in was cancelled.' : 'Something went wrong. Try again.');

export function Login({ mode }: { mode: 'login' | 'signup' }) {
  const qc = useQueryClient();
  const ssoError = new URLSearchParams(window.location.search).get('sso_error');
  const [error, setError] = useState<string | null>(ssoError ? (SSO_ERRORS[ssoError] ?? 'Single sign-on failed. Try again.') : null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState<{ challenge: string; methods: MfaMethod[] } | null>(ssoMfaFromFragment);
  const [sso, setSso] = useState(false);
  // "Keep me signed in" (remembered on this device): 30 days; off = until the browser or app is closed.
  const [remember, setRemember] = useState(() => { try { return localStorage.getItem('crm.remember') !== '0'; } catch { return true; } });
  const keep = (on: boolean) => { setRemember(on); try { localStorage.setItem('crm.remember', on ? '1' : '0'); } catch { /* private mode */ } };
  // The challenge is single-use: drop it from the address bar and history once read.
  useEffect(() => { if (window.location.hash.includes('mfa=')) history.replaceState(null, '', '/login'); }, []);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setFieldErrors({});
    try {
      await fn();
    } catch (err) {
      setError(errorText(err));
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors());
    } finally {
      setBusy(false);
    }
  };

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const body = Object.fromEntries(new FormData(e.currentTarget).entries());
    void run(async () => {
      if (sso) {
        const r = await api<{ sso: boolean; url?: string }>('/api/auth/sso/start', { method: 'POST', body: { email: body.email } });
        if (r.sso && r.url) { window.location.assign(r.url); return; }
        setSso(false);
        throw new ApiError(404, 'no_sso', 'Single sign-on isn’t set up for that email. Sign in with your password.');
      }
      const r = await api<{ mfaRequired?: boolean; challenge?: string; methods?: MfaMethod[] }>(mode === 'login' ? '/api/auth/login' : '/api/auth/signup', { method: 'POST', body: mode === 'login' ? { ...body, remember } : body });
      if (r?.mfaRequired && r.challenge) { setMfa({ challenge: r.challenge, methods: r.methods ?? ['totp'] }); return; }
      await qc.invalidateQueries({ queryKey: ['session'] });
    });
  }

  const passkeySignIn = () => run(async () => {
    const { handle, options } = await api<{ handle: string; options: Parameters<typeof startAuthentication>[0]['optionsJSON'] }>('/api/auth/passkeys/login/options', { method: 'POST', body: {} });
    const response = await startAuthentication({ optionsJSON: options });
    await api('/api/auth/passkeys/login/verify', { method: 'POST', body: { handle, response, remember } });
    await qc.invalidateQueries({ queryKey: ['session'] });
  });

  const field = (name: string, label: string, type = 'text', autoComplete?: string) => (
    <div className="field">
      <label htmlFor={`auth-${name}`}>{label}</label>
      <input id={`auth-${name}`} name={name} type={type} autoComplete={autoComplete} required aria-invalid={!!fieldErrors[name]} />
      {fieldErrors[name] && <span className="field-error">{fieldErrors[name]}</span>}
    </div>
  );

  if (mfa) return <MfaChallenge challenge={mfa.challenge} methods={mfa.methods} onRestart={(why) => { setMfa(null); setError(why); }} />;

  return (
    <main className="auth">
      <form className="auth-card" onSubmit={onSubmit} noValidate>
        <h1>{mode === 'login' ? (sso ? 'Sign in with SSO' : 'Sign in') : 'Create your organization'}</h1>
        {mode === 'signup' && field('organizationName', 'Organization name', 'text', 'organization')}
        {mode === 'signup' && field('name', 'Your name', 'text', 'name')}
        {field('email', sso ? 'Work email' : 'Email', 'email', mode === 'login' ? 'username webauthn' : 'email')}
        {!sso && field('password', 'Password', 'password', mode === 'login' ? 'current-password' : 'new-password')}
        {mode === 'signup' && <p className="hint">At least 8 characters: any letters, numbers or symbols you like.</p>}
        {mode === 'login' && !sso && (
          <div className="auth-row">
            <label className="radio"><input type="checkbox" checked={remember} onChange={(e) => keep(e.target.checked)} /> Keep me signed in</label>
            <Link className="hint" to="/forgot-password">Forgot your password?</Link>
          </div>
        )}
        {mode === 'login' && !sso && <p className="hint auth-keep-hint">{remember ? 'You stay signed in on this device for 30 days.' : 'You’ll be signed out when you close the browser or the app.'}</p>}
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? 'Please wait…' : sso ? 'Continue' : mode === 'login' ? 'Sign in' : 'Create organization'}
        </button>
        {mode === 'login' && (
          <div className="alt-signin">
            {browserSupportsWebAuthn() && !sso && <button type="button" className="btn" onClick={passkeySignIn} disabled={busy}>Sign in with a passkey</button>}
            <button type="button" className="btn ghost" onClick={() => { setSso((s) => !s); setError(null); }}>
              {sso ? 'Use a password instead' : 'Sign in with SSO'}
            </button>
          </div>
        )}
        <p className="hint">
          {mode === 'login' ? <>New here? <Link to="/signup">Create an organization</Link></> : <>Have an account? <Link to="/login">Sign in</Link></>}
        </p>
      </form>
    </main>
  );
}

/** Second sign-in step: a code from the authenticator app, a passkey, or a recovery code. */
export function MfaChallenge({ challenge, methods = ['totp'], onRestart }: { challenge: string; methods?: MfaMethod[]; onRestart: (why: string) => void }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'totp' | 'recovery'>(methods.includes('totp') ? 'totp' : 'recovery');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handle = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ['session'] });
    } catch (err) {
      setError(errorText(err));
      // Expired, or cancelled after too many wrong codes: back to the password, with the reason shown there.
      if (err instanceof ApiError && err.status === 410 && err.code === 'link_expired') onRestart(err.message);
    } finally {
      setBusy(false);
    }
  };

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const value = String(new FormData(e.currentTarget).get('code')).trim();
    void handle(() => api('/api/auth/mfa/verify', { method: 'POST', body: mode === 'recovery' ? { challenge, recoveryCode: value } : { challenge, code: value } }));
  }

  const usePasskey = () => handle(async () => {
    const { handle: h, options } = await api<{ handle: string; options: Parameters<typeof startAuthentication>[0]['optionsJSON'] }>('/api/auth/mfa/passkey/options', { method: 'POST', body: { challenge } });
    const response = await startAuthentication({ optionsJSON: options });
    await api('/api/auth/mfa/passkey/verify', { method: 'POST', body: { challenge, handle: h, response } });
  });

  return (
    <main className="auth">
      <form className="auth-card" onSubmit={onSubmit} noValidate>
        <h1>Verify it’s you</h1>
        {methods.includes('passkey') && (
          <button type="button" className="btn primary" onClick={usePasskey} disabled={busy}>Use your passkey</button>
        )}
        {(methods.includes('totp') || mode === 'recovery') && (
          <>
            <p className="muted">{mode === 'recovery' ? 'Enter one of your recovery codes.' : 'Open your authenticator app and enter the 6-digit code.'}</p>
            <div className="field">
              <label htmlFor="mfa-verify">{mode === 'recovery' ? 'Recovery code' : 'Code'}</label>
              <input id="mfa-verify" name="code" autoFocus={!methods.includes('passkey')} autoComplete="one-time-code" inputMode={mode === 'recovery' ? 'text' : 'numeric'} required />
            </div>
            <button className={methods.includes('passkey') ? 'btn' : 'btn primary'} disabled={busy}>{busy ? 'Checking…' : 'Verify'}</button>
          </>
        )}
        {error && <p className="form-error" role="alert">{error}</p>}
        {methods.includes('totp') && (
          <button type="button" className="btn ghost" onClick={() => setMode((m) => (m === 'recovery' ? 'totp' : 'recovery'))}>
            {mode === 'recovery' ? 'Use the authenticator app' : 'Use a recovery code'}
          </button>
        )}
      </form>
    </main>
  );
}
